import { InventoryDataEntryPlanningOrchestratorService } from 'orchestrator';
import { InventorySubTypeService, PropertyService, StandService, TeamService, TreeObservationService } from 'cbd-proxies';
import { ActionDeleteMeasurement } from './action-delete-measurement/action-delete-measurement';
import { ActionExecuteContinuousInventory } from './action-execute-continuous-inventory/action-execute-continuous-inventory';
import { ActionUndoContinuousInventory } from './action-undo-continuous-inventory/action-undo-continuous-inventory';
import { Catalog } from './services/catalog';
import {
  ActionOverRecordTypes,
  AdditionalGridColumnDefinition,
  createFormModalConfig,
  createLocalizationKey,
  FieldBuilder,
  FieldDefinition,
  IActionsSchema,
  IModal,
  MENU_CATEGORY_KEY,
  MENU_NAME_SUFFIX,
  MENU_SUBCATEGORY_KEY,
  MenuCategories,
  MenuSubcategories,
  modalAction,
  triggerAction,
  ZendraLocalizationServices,
} from '@zendra/shared';

FieldBuilder.setNamespace('Inventory');

const buildReadOnlyGridNumberColumn = (
  key: string,
  label: string,
): AdditionalGridColumnDefinition => ({
  key,
  label,
  hiddenByDefault: true,
  column: {
    field: key,
    sortable: false,
    filterable: false,
    minWidth: 120,
    params: {
      excludeFromExport: true,
    },
  },
});

const standNumberField = FieldBuilder
  .text('standNumber', 'Entity.InventoryDataEntry.StandNumber')
  .required()
  .maxLength(10)
  .build();

// Keep the imported stand number and its form validation; only the search uses the stand catalog.
standNumberField.column.createFilter = FieldBuilder
  .foreignKey('standNumber', 'Entity.InventoryDataEntry.StandNumber', StandService)
  .display('name')
  .keyField('name')
  .displayFromNested('standNumber')
  .loadWith((service, query, config) => {
    if (Array.isArray(query.name) && query.name.length) {
      return service.findByNames({ names: query.name.map(String) }, config);
    }

    return service.getList(query, config);
  })
  .build().column.createFilter;

standNumberField.additionalGridColumns = [
  buildReadOnlyGridNumberColumn('standId', 'Stand ID'),
];

export const INVENTORYDATAENTRY_FIELDS: FieldDefinition[] = [
  FieldBuilder
    .date('measurementDate', 'Entity.InventoryDataEntry.MeasurementDate')
    .required()
    .build(),
  standNumberField,
  FieldBuilder.foreignKey('propertyId', 'Entity.Property', PropertyService)
    .display('name')
    .displayFromNested('propertyName')
    .sortBy('propertyName')
    .gridColumnMinWidth(120)
    .hideInChildForms()
    .readOnly()
    .build(),
  FieldBuilder
    .text('regionName', 'Entity.Region')
    .hideInChildForms()
    .readOnly()
    .build(),
  FieldBuilder
    .number('rotationNumber', 'Entity.Rotation.RotationValue')
    .hideInChildForms()
    .readOnly()
    .build(),
  FieldBuilder
    .text('species', 'Entity.InventoryDataEntry.Species')
    .required()
    .maxLength(50)
    .build(),
  FieldBuilder
    .number('plotNumber', 'Entity.InventoryDataEntry.PlotNumber')
    .required()
    .build(),
  FieldBuilder
    .number('treeNumber', 'Entity.InventoryDataEntry.TreeNumber')
    .required()
    .build(),
  FieldBuilder
    .number('height', 'Entity.InventoryDataEntry.Height')
    .decimal()
    .build(),
  FieldBuilder
    .number('averageHeightDiameter', 'Entity.InventoryDataEntry.AverageHeightDiameter')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .number('heightDiameterFirstMeasurement', 'Entity.InventoryDataEntry.HeightDiameterFirstMeasurement')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .number('heightDiameterSecondMeasurement', 'Entity.InventoryDataEntry.HeightDiameterSecondMeasurement')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .number('radius', 'Entity.InventoryDataEntry.Radius')
    .decimal()
    .build(),
  FieldBuilder
    .number('meditionNumber', 'Entity.InventoryDataEntry.MeditionNumber')
    .required()
    .build(),
  FieldBuilder
    .checkbox('isProcessed', 'Entity.InventoryDataEntry.IsProcessed')
    .build(),
  FieldBuilder
    .checkbox('shouldPick', 'Entity.InventoryDataEntry.ShouldPick')
    .build(),
  FieldBuilder
    .number('betweenLines1', 'Entity.InventoryDataEntry.BetweenLines1')
    .decimal()
    .build(),
  FieldBuilder
    .number('betweenLines2', 'Entity.InventoryDataEntry.BetweenLines2')
    .decimal()
    .build(),
  FieldBuilder
    .number('betweenLines3', 'Entity.InventoryDataEntry.BetweenLines3')
    .decimal()
    .build(),
  FieldBuilder.foreignKey(
    'treeObservationId',
    'Entity.InventoryDataEntry.TreeObservationId',
    TreeObservationService,
  )
    .display('name')
    .displayFromNested('treeObservationName')
    .sortBy('treeObservationName')
    .hideInChildForms()
    .readOnly()
    .build(),
  FieldBuilder
    .number('measurementOrderNumber', 'Entity.InventoryDataEntry.MeasurementOrderNumber')
    .build(),
  FieldBuilder.foreignKey(
    'teamId',
    'Entity.Team',
    TeamService,
  )
    .display('name')
    .displayFromNested('teamName')
    .sortBy('teamName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'inventorySubTypeId',
    'Entity.InventorySubType',
    InventorySubTypeService,
  )
    .display('name')
    .displayFromNested('inventorySubTypeName')
    .sortBy('inventorySubTypeName')
    .readOnly()
    .build(),
];

const namespace = ZendraLocalizationServices.Inventory;

export const INVENTORY_DATA_ENTRY_ACTION_IDS = {
  importExcelModal: 0,
  downloadExcelTemplateTrigger: 1,
  deleteMeasurement: 2,
  executeContinuousInventory: 3,
  undoContinuousInventory: 4,
};

const ACTION_DELETE_MEASUREMENT_WRAPPER: IModal = createFormModalConfig({
  component: ActionDeleteMeasurement,
  title: createLocalizationKey('Action.Modal.DeleteMeasurementInventoryDataEntry.Title', namespace),
  confirmLabel: createLocalizationKey('Confirm', ZendraLocalizationServices.LanguageService),
  subTitle: createLocalizationKey('Entity.InventoryDataEntry', namespace),
  asideConfig: { show: false, data: {}, title: '' },
  inputs: {
    deleteService: InventoryDataEntryPlanningOrchestratorService,
    catalogService: Catalog,
    measurementLabelKey: createLocalizationKey('Entity.InventoryDataEntry.MeditionNumberDelete', namespace),
    successMessageKey: 'Actions:DeleteMeasurementInventoryDataEntrySuccess',
    useLidar: false,
  },
});

const ACTION_EXECUTE_CONTINUOUS_INVENTORY_WRAPPER: IModal = createFormModalConfig({
  component: ActionExecuteContinuousInventory,
  title: createLocalizationKey('Action.Modal.ExecuteContinuousInventoryDataEntry.Title', namespace),
  confirmLabel: createLocalizationKey('Confirm', ZendraLocalizationServices.LanguageService),
  subTitle: createLocalizationKey('Entity.InventoryDataEntry', namespace),
  asideConfig: { show: false, data: {}, title: '' },
  inputs: {
    executeService: InventoryDataEntryPlanningOrchestratorService,
    catalogService: {
      useFactory: (catalog: Catalog) => {
        catalog.useLidar = false;
        return catalog;
      },
      deps: [Catalog],
    },
    measurementLabelKey: createLocalizationKey('Entity.InventoryDataEntry.MeditionNumberProcess', namespace),
  },
});

const ACTION_UNDO_CONTINUOUS_INVENTORY_WRAPPER: IModal = createFormModalConfig({
  component: ActionUndoContinuousInventory,
  title: createLocalizationKey('Action.Modal.UndoContinuousInventoryDataEntry.Title', namespace),
  confirmLabel: createLocalizationKey('Confirm', ZendraLocalizationServices.LanguageService),
  subTitle: createLocalizationKey('Entity.InventoryDataEntry', namespace),
  asideConfig: { show: false, data: {}, title: '' },
  inputs: {
    undoService: InventoryDataEntryPlanningOrchestratorService,
    catalogService: Catalog,
    measurementLabelKey: createLocalizationKey('Entity.InventoryDataEntry.MeditionNumberToUnprocess', namespace),
    standLabelKey: createLocalizationKey('Entity.InventoryDataEntry.Stands', namespace),
  },
});

export const INVENTORYDATAENTRY_ACTIONS: IActionsSchema = {
  overTable: [
    triggerAction(
      INVENTORY_DATA_ENTRY_ACTION_IDS.importExcelModal,
      'Action.Modal.ImportMeasurementInventoryDataEntry',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.Inventory,
    ),
    triggerAction(
      INVENTORY_DATA_ENTRY_ACTION_IDS.downloadExcelTemplateTrigger,
      'Actions:ExcelTemplateDownload',
      ActionOverRecordTypes.OverTable,
      ZendraLocalizationServices.LanguageService,
    ),
    modalAction(
      INVENTORY_DATA_ENTRY_ACTION_IDS.executeContinuousInventory,
      'Action.Modal.ExecuteContinuousInventoryDataEntry',
      ActionOverRecordTypes.OverTable,
      ACTION_EXECUTE_CONTINUOUS_INVENTORY_WRAPPER,
      namespace,
    ),
    modalAction(
      INVENTORY_DATA_ENTRY_ACTION_IDS.undoContinuousInventory,
      'Action.Modal.UndoContinuousInventoryDataEntry',
      ActionOverRecordTypes.OverTable,
      ACTION_UNDO_CONTINUOUS_INVENTORY_WRAPPER,
      namespace,
    ),
    modalAction(
      INVENTORY_DATA_ENTRY_ACTION_IDS.deleteMeasurement,
      'Action.Modal.DeleteMeasurementInventoryDataEntry',
      ActionOverRecordTypes.OverTable,
      ACTION_DELETE_MEASUREMENT_WRAPPER,
      namespace
    )
  ],
};

export const INVENTORYDATAENTRY_CONFIG = {
  entityName: 'InventoryDataEntry',
  localizationKey: 'Inventory::Entity.InventoryDataEntry',
  fields: INVENTORYDATAENTRY_FIELDS,
  permission: 'PlanningInventory.InventoryDataEntries',
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Inventory + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.TransactionsInventory + MENU_NAME_SUFFIX,
  actions: INVENTORYDATAENTRY_ACTIONS,
  actionIds: INVENTORY_DATA_ENTRY_ACTION_IDS,
};
